import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from "@angular/common/http"
import {Injectable} from "@angular/core"
import {Observable, of, throwError} from "rxjs"
import {ErrorService} from "app/common/services/error/service/ErrorService"
import {Optional} from "app/common/util/Optional"
import {catchError, flatMap, map} from "rxjs/operators"

type AlertFn = (response: HttpErrorResponse) => Observable<HttpErrorResponse>;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    private readonly alerts: Map<number, AlertFn>

    public constructor(private service: ErrorService) {
        this.alerts = this.setupAlerts()
    }

    private setupAlerts(): Map<number, AlertFn> {
        const alerts = new Map<number, AlertFn>()
        alerts.set(417, this.expectationFailed.bind(this))
        alerts.set(500, this.serverError.bind(this))
        alerts.set(503, this.serviceUnavailable.bind(this))
        alerts.set(401, this.noOpHandler.bind(this))
        alerts.set(403, this.noOpHandler.bind(this))

        return alerts
    }

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(catchError((err) => this.handleErrorResponse(err)))
    }

    private handleErrorResponse(response: HttpErrorResponse): Observable<any> {
        return Optional.of(this.alerts.get(response.status))
                       .map((alertFn) => alertFn(response))
                       .orElseGet(() => this.unhandledError(response))
                       .pipe(flatMap(() => throwError(response)))
    }

    private serverError(response: HttpErrorResponse): Observable<HttpErrorResponse> {
        return this.service.open("There was an error processing your request", "Unexpected Error")
                   .pipe(map(() => response))
    }

    private serviceUnavailable(response: HttpErrorResponse): Observable<HttpErrorResponse> {
        return this.service.open("The service is currently unavailable. Please try again later", "Service Unavailable")
                   .pipe(map(() => response))
    }

    private expectationFailed(response: HttpErrorResponse): Observable<HttpErrorResponse> {
        const title = Optional.of(response.error)
                              .map((err) => err.title)
                              .orNull()
        const message = this.errorMessage(response)
        return this.service.open(message, title)
                   .pipe(map(() => response))
    }

    private unhandledError(response: HttpErrorResponse): Observable<HttpErrorResponse> {
        return this.service.open(response.message, "Unexpected Error")
                   .pipe(map(() => response))
    }

    private noOpHandler(response: HttpErrorResponse): Observable<HttpErrorResponse> {
        return of(response)
    }

    private errorMessage(response: HttpErrorResponse): string {
        return Optional.of(response.error)
                       .map((err) => err.message)
                       .orElse("An error occurred processing your request")
    }
}
